import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Footer } from '../components/Footer';
import { toast } from 'sonner';
import authService from '../services/authService';
import logo from '../Logo.svg';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s\-().]{6,19}$/;

const EMPTY = {
  firstName: '',
  lastName: '',
  email: '',
  organizationName: '',
  phoneNumber: '',
  password: '',
  confirmPassword: '',
};

export default function RegisterPage({ setIsAuthenticated }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validate = (data) => {
    const e = {};
    if (!data.firstName.trim())        e.firstName = 'First name is required.';
    if (!data.lastName.trim())         e.lastName = 'Last name is required.';
    if (!data.email.trim())            e.email = 'Email is required.';
    else if (!EMAIL_RE.test(data.email.trim())) e.email = 'Enter a valid email address.';
    if (!data.organizationName.trim()) e.organizationName = 'Organization name is required.';
    if (!data.phoneNumber.trim())      e.phoneNumber = 'Phone number is required.';
    else if (!PHONE_RE.test(data.phoneNumber.trim())) e.phoneNumber = 'Enter a valid phone number (e.g. +1 555 123-4567).';
    if (!data.password)                e.password = 'Password is required.';
    else if (data.password.length < 6) e.password = 'Password must be at least 6 characters.';
    if (!data.confirmPassword)         e.confirmPassword = 'Please confirm your password.';
    else if (data.password && data.password !== data.confirmPassword) e.confirmPassword = 'Passwords do not match.';
    return e;
  };

  const handleChange = (e) => {
    const updated = { ...formData, [e.target.name]: e.target.value };
    setFormData(updated);
    if (errors[e.target.name]) setErrors(prev => ({ ...prev, [e.target.name]: undefined }));
    // Re-check confirmPassword live when password changes
    if (e.target.name === 'password' && errors.confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: undefined }));
    }
  };

  const handleBlur = (e) => {
    const errs = validate(formData);
    if (errs[e.target.name]) setErrors(prev => ({ ...prev, [e.target.name]: errs[e.target.name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(formData);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setIsLoading(true);
    try {
      const response = await authService.register(formData);
      if (setIsAuthenticated) setIsAuthenticated(true);
      toast.success(`Welcome, ${response.name}! Your account has been created.`);
      navigate('/subscription');
    } catch (error) {
      toast.error(error || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const err = (field) => errors[field]
    ? <p className="text-xs text-destructive mt-0.5">{errors[field]}</p>
    : null;

  const inputCls = (field) =>
    errors[field] ? 'border-destructive focus-visible:ring-destructive' : '';

  return (
    <div className="min-h-screen gradient-hero flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <Card className="w-full max-w-2xl animate-scale-in">
          <CardHeader className="space-y-4">
            <div className="flex justify-center">
              <Link to="/">
                <img src={logo} alt="Verdionix" className="h-14 w-auto" style={{ mixBlendMode: 'multiply' }} />
              </Link>
            </div>
            <div className="text-center">
              <CardTitle className="text-2xl font-display">Create Your Account</CardTitle>
              <CardDescription>Get started with Verdionix today</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="firstName">First Name <span className="text-destructive">*</span></Label>
                  <Input id="firstName" name="firstName" placeholder="John"
                    value={formData.firstName} onChange={handleChange} onBlur={handleBlur}
                    className={inputCls('firstName')} />
                  {err('firstName')}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="lastName">Last Name <span className="text-destructive">*</span></Label>
                  <Input id="lastName" name="lastName" placeholder="Doe"
                    value={formData.lastName} onChange={handleChange} onBlur={handleBlur}
                    className={inputCls('lastName')} />
                  {err('lastName')}
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
                <Input id="email" name="email" type="email" placeholder="you@company.com"
                  value={formData.email} onChange={handleChange} onBlur={handleBlur}
                  className={inputCls('email')} />
                {err('email')}
              </div>

              <div className="space-y-1">
                <Label htmlFor="organizationName">Organization Name <span className="text-destructive">*</span></Label>
                <Input id="organizationName" name="organizationName" placeholder="Your Company Inc."
                  value={formData.organizationName} onChange={handleChange} onBlur={handleBlur}
                  className={inputCls('organizationName')} />
                {err('organizationName')}
              </div>

              <div className="space-y-1">
                <Label htmlFor="phoneNumber">Phone Number <span className="text-destructive">*</span></Label>
                <Input id="phoneNumber" name="phoneNumber" type="tel" placeholder="+1 (555) 123-4567"
                  value={formData.phoneNumber} onChange={handleChange} onBlur={handleBlur}
                  className={inputCls('phoneNumber')} />
                {err('phoneNumber')}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="password">Password <span className="text-destructive">*</span></Label>
                  <Input id="password" name="password" type="password" placeholder="Min. 6 characters"
                    value={formData.password} onChange={handleChange} onBlur={handleBlur}
                    className={inputCls('password')} />
                  {err('password')}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="confirmPassword">Confirm Password <span className="text-destructive">*</span></Label>
                  <Input id="confirmPassword" name="confirmPassword" type="password" placeholder="Repeat password"
                    value={formData.confirmPassword} onChange={handleChange} onBlur={handleBlur}
                    className={inputCls('confirmPassword')} />
                  {err('confirmPassword')}
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Creating account…' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Already have an account? </span>
              <Link to="/login" className="text-primary hover:underline font-medium">Sign in</Link>
            </div>
            <div className="mt-4 text-center">
              <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition">← Back to Home</Link>
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
}
